// src/components/PricingPreview.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { IoCheckmark } from 'react-icons/io5';
import { fetchPricingPlans } from '../services/apiService';

// --- Conversion & Formatting Helpers ---
const CONVERSION_RATE_INR_TO_USD = 83;

const formatCurrency = (price, currency = 'INR') => {
    if (typeof price !== 'number') return price;

    if (currency === 'INR') {
        const priceInUsd = Math.round((price / 100) / CONVERSION_RATE_INR_TO_USD);
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
        }).format(priceInUsd);
    }
    
    // Fallback for other currencies
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 0,
    }).format(price / 100);
};

const transformPreviewPlan = (planData) => {
    const tier = planData.monthly;
    if (!tier) return null;
    return {
        name: tier.plan_name,
        price: formatCurrency(tier.plan_price, tier.plan_currency),
        description: tier.plan_description,
        features: [
            `${tier.brands_limit} Brand Profiles`,
            `${tier.ai_social_post_credits_limit} AI Post Credits/mo`,
            `${tier.storage_gb_limit} GB Storage`,
            tier.support_level ? `${tier.support_level} Support` : 'Community Support',
        ],
        href: planData.isCustom ? "/schedule-demo" : "https://app.oolook.in/signup",
    };
};

// --- Component ---
const PricingPreview = () => {
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [activeTier, setActiveTier] = useState('');

    useEffect(() => {
        const loadPlans = async () => {
            try {
                const apiPlans = await fetchPricingPlans();

                // Sort plans: Free -> Paid (low to high) -> Custom
                apiPlans.sort((a, b) => {
                    if (a.isFree) return -1;
                    if (b.isFree) return 1;
                    if (a.isCustom) return 1;
                    if (b.isCustom) return -1;
                    return (a.monthly?.plan_price || 0) - (b.monthly?.plan_price || 0);
                });

                const processedPlans = [];
                const freePlan = apiPlans.find(p => p.isFree);
                const enterprisePlan = apiPlans.find(p => p.isCustom);
                const paidPlans = apiPlans.filter(p => !p.isFree && !p.isCustom);
                
                if (freePlan) {
                    processedPlans.push({
                        id: 'free', name: 'Free', isGroup: false, mostPopular: false, ...transformPreviewPlan(freePlan)
                    });
                }

                if (paidPlans.length >= 2) {
                    const firstPaidPlan = paidPlans[0];
                    const secondPaidPlan = paidPlans[1];
                    setActiveTier(firstPaidPlan.base_plan_name); // Set initial active tier

                    processedPlans.push({
                        id: 'pro_group', name: 'Paid Plan', isGroup: true, mostPopular: true,
                        options: {
                            [firstPaidPlan.base_plan_name]: transformPreviewPlan(firstPaidPlan),
                            [secondPaidPlan.base_plan_name]: transformPreviewPlan(secondPaidPlan),
                        },
                        groupNames: [firstPaidPlan.base_plan_name, secondPaidPlan.base_plan_name],
                    });
                }

                if (enterprisePlan) {
                    processedPlans.push({
                        id: 'enterprise', name: 'Enterprise', isGroup: false, mostPopular: false, ...transformPreviewPlan(enterprisePlan)
                    });
                }
                
                setPlans(processedPlans.slice(0, 3));

            } catch (err) {
                setError('Could not load pricing information.');
            } finally {
                setLoading(false);
            }
        };

        loadPlans();
    }, []);

    return (
        <section className="py-24 sm:py-32">
            <div className="mx-auto max-w-7xl px-6 lg:px-8">
                <div className="mx-auto max-w-4xl text-center">
                    <h2 className="text-3xl font-bold tracking-tight text-dark-text-primary sm:text-4xl">
                        Simple, transparent pricing
                    </h2>
                    <p className="mt-4 text-lg text-dark-text-secondary">
                        Choose the plan that's right for you. Start for free, no credit card required.
                    </p>
                </div>

                {loading && <div className="text-center mt-16 text-dark-text-secondary">Loading pricing...</div>}
                {error && <div className="text-center mt-16 text-red-400">{error}</div>}
                
                {!loading && !error && plans.length > 0 && (
                    <div className="isolate mx-auto mt-16 grid max-w-md grid-cols-1 gap-8 lg:mx-0 lg:max-w-none lg:grid-cols-3">
                        {plans.map((plan) => {
                             const displayData = plan.isGroup ? plan.options[activeTier] : plan;
                             if (!displayData) return null; // Don't render if data is missing

                            return (
                                <div key={plan.id} className={`flex flex-col rounded-3xl p-8 ring-1 transition-all duration-300 ease-in-out hover:scale-105 ${plan.mostPopular ? 'ring-2 ring-dark-accent-start bg-dark-card' : 'ring-white/10 bg-dark-card/40'}`}>
                                    {plan.isGroup && (
                                        <div className="mb-4 flex self-center rounded-full p-1 bg-dark-bg/50 border border-white/10">
                                            {plan.groupNames.map(name => (
                                                <button key={name} onClick={() => setActiveTier(name)} className={`px-3 py-1 text-xs font-semibold rounded-full ${activeTier === name ? 'bg-dark-accent-start/80 text-white' : 'text-dark-text-secondary'}`}>{name}</button>
                                            ))}
                                        </div>
                                    )}
                                    <h3 className="text-lg font-semibold leading-8 text-dark-text-primary">{displayData.name}</h3>
                                    <p className="mt-4 text-sm leading-6 text-dark-text-secondary">{displayData.description}</p>
                                    <p className="mt-6 flex items-baseline gap-x-1">
                                        <span className="text-4xl font-bold tracking-tight text-dark-text-primary">{displayData.price}</span>
                                        {displayData.price !== "Custom" && displayData.price !== "$0" && <span className="text-sm font-semibold leading-6 text-dark-text-secondary">/month</span>}
                                    </p>
                                    <Link to={displayData.href} className={`mt-6 block rounded-md py-2 px-3 text-center text-sm font-semibold leading-6 ${plan.mostPopular ? 'bg-dark-accent-start text-white shadow-sm hover:opacity-90' : 'bg-white/10 text-white hover:bg-white/20'}`}>
                                        {plan.id === "enterprise" ? "Contact Sales" : "Get Started"}
                                    </Link>
                                    <ul className="mt-8 space-y-3 text-sm leading-6 text-dark-text-secondary flex-1">
                                        {displayData.features.map(feature => (
                                            <li key={feature} className="flex gap-x-3">
                                                <IoCheckmark className="h-6 w-5 flex-none text-dark-accent-start" />
                                                {feature}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )
                        })}
                    </div>
                )}
                <div className="text-center mt-12">
                    <Link to="/pricing" className="text-sm font-semibold text-dark-text-primary hover:text-white">
                        View all features & compare plans &rarr;
                    </Link>
                </div>
            </div>
        </section>
    );
};

export default PricingPreview;